import { type } from 'arktype';
import type { ShiftOverrideApprovalInfo } from '@zag/core/models';
import { weekdayAssignmentsSchema } from './plan-version-data.model';
import { shiftSkillRequirementSchema } from './shift-rules.model';
import { isoWeekdaySchema } from './strategy.model';
import type { ScheduleMap } from './plan-version-data.model';
import type { ConflictResult } from '../validation/conflict.types';
import type { ProvenanceMap } from './assignment.types';

// `ScheduleMap` now lives in `plan-version-data.model` to avoid an import cycle
// with `conflict.types`. Re-exported here for backward compatibility with the
// many modules that import it from this file.
export type { ScheduleMap } from './plan-version-data.model';

const isoUtcDateTimeSchema = type('string').narrow((value, ctx) => {
  const isIsoUtcDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value) && !Number.isNaN(Date.parse(value));
  return isIsoUtcDateTime || ctx.mustBe('an ISO 8601 UTC datetime');
});

const assignedShiftUnitDescriptorShiftSchema = type({
  '+': 'reject',
  id: 'string',
  name: 'string',
  capacity: 'number>=0',
  'requiredSkillIds?': 'string[]',
  'requiredSkillEntries?': shiftSkillRequirementSchema.array(),
  'allowOverlap?': 'boolean',
  'icon?': 'string | null',
  'color?': 'string | null',
  'validWeekdays?': isoWeekdaySchema.array(),
}).narrow((shift, ctx) => {
  const { requiredSkillIds, requiredSkillEntries } = shift;
  if (requiredSkillIds === undefined || requiredSkillEntries === undefined) return true;

  const entrySkillIds = requiredSkillEntries.map(entry => entry.skillId);
  const skillsMatch =
    requiredSkillIds.length === entrySkillIds.length &&
    new Set(requiredSkillIds).size === new Set(entrySkillIds).size &&
    requiredSkillIds.every(skillId => entrySkillIds.includes(skillId));
  return skillsMatch || ctx.mustBe('requiredSkillIds matching requiredSkillEntries');
});

export const assignedShiftUnitDescriptorSchema = type({
  '+': 'reject',
  id: 'string',
  shiftId: 'string',
  startTime: isoUtcDateTimeSchema,
  endTime: isoUtcDateTimeSchema,
  deletedAt: 'string | null',
  shift: assignedShiftUnitDescriptorShiftSchema,
});

export type AssignedShiftUnitDescriptor = typeof assignedShiftUnitDescriptorSchema.t;

/**
 * Expanded schedule response from the plan instance expansion endpoint.
 *
 * This is the result of expanding concrete instance assignments into a
 * date-keyed schedule for a specific date range.
 */
export const expandedScheduleSchema = type({
  instanceId: type('string').configure({
    description: 'ID of the plan instance that produced this schedule.',
    examples: ['planinst_abc123'],
  }),
  planVersionId: type('string | null').configure({
    description: 'Optional source provenance retained on the instance.',
    examples: ['planver_abc123'],
  }),
  startDate: type('string').configure({
    description: 'Inclusive start of the expanded range as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  endDate: type('string').configure({
    description: 'Inclusive end of the expanded range as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  timezone: type('string').configure({
    description: 'IANA timezone identifier used during expansion.',
    examples: ['Europe/Zurich'],
  }),
  schedule: type({
    '[/^\\d{4}-\\d{2}-\\d{2}$/]': weekdayAssignmentsSchema,
  }).configure({
    description: 'Per-date assignment map keyed by ISO calendar date (YYYY-MM-DD). Each value is an employee→shift-unit map.',
  }),
  'assignedShiftUnitDescriptors?': assignedShiftUnitDescriptorSchema.array(),
  closedDates: type('string[]').configure({
    description: 'ISO calendar dates (YYYY-MM-DD) on which at least one shift unit is closed.',
    examples: [['2026-04-25', '2026-04-26']],
  }),
  capacityOverrides: type({
    '[/^\\d{4}-\\d{2}-\\d{2}$/]': {
      '[string]': 'number>=0', // shiftUnitId → overridden capacity
    },
  }).configure({
    description: 'Per-date capacity overrides: map of ISO date (YYYY-MM-DD) → shiftUnitId → overridden capacity.',
  }),
  'shiftUnitOverrides?': type({
    '[/^\\d{4}-\\d{2}-\\d{2}$/]': {
      '[string]': {
        '[string]': {
          'startTime?': 'string',
          'endTime?': 'string',
          'locationId?': 'string | null',
        },
      },
    },
  }).configure({
    description:
      'Per-date per-employee time/location overrides keyed as: date → shiftUnitId → employeeId → override. Populated when employees have overridden their assigned shift times or locations.',
  }),
}).configure({ description: 'Expanded (date-keyed) schedule derived from concrete instance assignments and person defaults.' });

export type ExpandedSchedule = typeof expandedScheduleSchema.t & {
  /** Provenance metadata: date → personId → assignment source. Present when defaults are active. */
  provenance?: ProvenanceMap;
  /**
   * Suppressed person-plan defaults per date.
   * date → personId → shiftUnitIds the person would have been assigned to.
   */
  suppressedAssignments?: Record<string, Record<string, string[]>>;
  /**
   * Person-plan default patterns indexed by employeeId → ISO weekday → shiftUnitIds.
   * Lets the client render the correct default shift immediately after a manual
   * removal instead of waiting for the autosave PATCH + schedule refetch.
   */
  personDefaultsByWeekday?: Record<string, Record<number, string[]>>;
  /**
   * Present only when `includeConflicts=true` triggered a first-time
   * `computeAndPersistForInstance` call (i.e. `conflictsComputedAt` was null).
   * The computation bumps the row's `updatedAt`; callers must propagate this
   * value into their local instance state so the next PATCH's
   * `baseInstanceUpdatedAt` matches the server — otherwise every first drag
   * after a fresh page load produces a spurious 409.
   */
  instanceUpdatedAt?: string;
  /** Approval state of employee time-range overrides: date → shiftUnitId → employeeId → info. */
  shiftOverrideApprovals?: ShiftOverrideApprovalsMap;
};

export type ShiftOverrideApprovalsMap = Record<string, Record<string, Record<string, ShiftOverrideApprovalInfo>>>;

/**
 * Type-safe capacity overrides structure.
 */
export type CapacityOverridesMap = Record<string, Record<string, number>>;

/**
 * Date+unit+employee-keyed time/location overrides.
 *
 *   { [date]: { [shiftUnitId]: { [employeeId]: { startTime?, endTime?, breakMinutes?, locationId? } } } }
 *
 * Each assigned employee can override their own time/location independently;
 * the override does NOT propagate to other employees assigned to the same unit.
 */
export type ShiftUnitEffectiveOverridesMap = Record<
  string,
  Record<string, Record<string, { startTime?: string; endTime?: string; breakMinutes?: number; locationId?: string | null }>>
>;

/**
 * Result of the schedule expansion algorithm.
 */
export interface ExpandedScheduleResult {
  schedule: ScheduleMap;
  assignedShiftUnitDescriptors?: AssignedShiftUnitDescriptor[];
  closedDates: string[];
  capacityOverrides: CapacityOverridesMap;
  shiftUnitOverrides?: ShiftUnitEffectiveOverridesMap;
  /** Holiday labels keyed by date, independent of whether conflicts currently exist. */
  holidayDates?: Record<string, string>;
  /** Present when `?includeConflicts=true` is passed to the schedule endpoint. */
  conflicts?: ConflictResult[];
  /**
   * Suppressed person-plan defaults per date.
   * date → personId → shiftUnitIds the person would have been assigned to.
   * Populated when defaults are active and at least one was suppressed.
   */
  suppressedAssignments?: Record<string, Record<string, string[]>>;
}

/**
 * Query parameters for the batch conflicts endpoint.
 */
export interface ConflictQueryParams {
  planId: string;
  startDate: string;
  endDate: string;
}
